import { useState, useEffect, useRef } from "react";
import { useSession } from "next-auth/react";
import moment from "moment-timezone";
import QuestItem from "./QuestItem";
import QuestTimer from "./QuestTimer";
import CurrentEventBanner from "./CurrentEventBanner";
import { useTranslations } from "next-intl";
import { PASTEL_SOLID_CARD } from "@/components/Pedagogy/pastel/pastel";
import useAvatarEvent from "@/components/AvatarEvent/useAvatarEvent";
import { totalPieces } from "@/lib/avatar/events";
import EventQuestsHeader from "@/components/AvatarEvent/EventQuestsHeader";
import { useLocale } from "next-intl";
import { QUESTS_REFRESH_EVENT, QUESTS_MINUTE_EVENT, flushLearningTime } from "@/lib/questsRefresh";

const QuestSkeleton = () => (
    <div className="space-y-3">
        {[1, 2, 3, 4].map((i) => (
            <div
                key={i}
                className="bg-white dark:bg-white/5 rounded-lg border border-gray-100 dark:border-white/10 p-3 animate-pulse"
            >
                <div className="flex items-start gap-3">
                    <div className="w-5 h-5 bg-gray-200 dark:bg-white/10 rounded" />
                    <div className="flex-1">
                        <div className="h-4 bg-gray-200 dark:bg-white/10 rounded w-3/4 mb-2" />
                        <div className="h-4 bg-gray-200 dark:bg-white/10 rounded w-full" />
                    </div>
                    <div className="w-5 h-5 bg-gray-200 dark:bg-white/10 rounded" />
                </div>
            </div>
        ))}
    </div>
);

// variant="pastel" : habillage de l'onglet Progression d'une formation (charte
// pastel). Sans variant, le rendu du tableau de bord et de Reps ne bouge pas.
export default function QuestList({ cardClass, titleFontSize, showIcon = false, variant }) {
    const { data: session } = useSession();
    const t = useTranslations();
    const [quests, setQuests] = useState([]);
    const [currentEvent, setCurrentEvent] = useState(null);
    const [loading, setLoading] = useState(true);
    // Ce que chaque quête vient de gagner depuis la lecture précédente : la ligne
    // le montre en pastilles qui rejoignent le compteur. Rien au premier chargement.
    const [gains, setGains] = useState({});
    const lastProgressRef = useRef(null);
    const questsRef = useRef([]);

    useEffect(() => {
        questsRef.current = quests;
    }, [quests]);
    // Pendant un événement de puzzles, les quêtes prennent son habillage : titre, bandeau, coffres.
    const locale = useLocale();
    const eventState = useAvatarEvent(Boolean(session?.user));
    const liveEvent = eventState?.event?.phase === "open" ? eventState.event : null;
    // Il reste des pièces à gagner tant que tous les puzzles ne sont pas finis : il n'y a jamais de doublon.
    const pieceLeft = Boolean(liveEvent) && (eventState.mine?.total || 0) < totalPieces(liveEvent);
    const questsTitle = liveEvent?.theme?.questsTitle?.[locale === "en" ? "en" : "fr"] || t("quests_of_the_day");

    useEffect(() => {
        if (session?.user) {
            fetchDailyQuests();
            const interval = setInterval(fetchDailyQuests, 30000);
            // Session passée ou cochée : le back vient de compter, on relit sans attendre.
            window.addEventListener(QUESTS_REFRESH_EVENT, fetchDailyQuests);
            window.addEventListener(QUESTS_MINUTE_EVENT, addWatchedMinute);
            return () => {
                clearInterval(interval);
                window.removeEventListener(QUESTS_REFRESH_EVENT, fetchDailyQuests);
                window.removeEventListener(QUESTS_MINUTE_EVENT, addWatchedMinute);
            };
        }
    }, [session]);

    const fetchDailyQuests = async () => {
        if (!session?.user) return;

        try {
            const response = await fetch("/api/quests/daily", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    timezone: session.user.timezone || moment.tz.guess(),
                }),
            });
            const data = await response.json();

            if (data.quests) {
                const previous = lastProgressRef.current;
                const next = {};
                const fresh = {};
                const merged = data.quests.map((quest) => {
                    const progress = Number(quest.currentProgress) || 0;
                    const before = previous?.[quest.id];
                    // Une réponse en retard peut être plus ancienne : on garde le maximum.
                    next[quest.id] = before !== undefined ? Math.max(before, progress) : progress;
                    if (before !== undefined && progress > before) {
                        fresh[quest.id] = { amount: progress - before, key: `${quest.id}-${progress}` };
                    }
                    // Les minutes déjà montrées pendant la vidéo ne reculent pas tant que
                    // le temps n'est pas parti au back : l'affichage garde le maximum.
                    const done = quest.completed_at || quest.completed;
                    return !done && next[quest.id] > progress ? { ...quest, currentProgress: next[quest.id] } : quest;
                });
                lastProgressRef.current = next;
                if (Object.keys(fresh).length > 0) {
                    setGains((current) => ({ ...current, ...fresh }));
                }
                setQuests(merged);
            }
            if (data.currentEvent) {
                setCurrentEvent(data.currentEvent);
            } else {
                setCurrentEvent(null);
            }
        } catch (error) {
            console.error("Error fetching quests:", error);
        } finally {
            setLoading(false);
        }
    };

    // Une minute de vidéo vient d'être regardée : la quête de temps avance à l'écran,
    // sans étiquette (on regarde la vidéo, pas les quêtes) et sans appel au serveur.
    const addWatchedMinute = () => {
        const fresh = {};
        let finished = false;
        const next = questsRef.current.map((quest) => {
            const progress = Number(quest.currentProgress) || 0;
            if (quest.type !== "time_spent" || quest.completed_at || quest.completed) return quest;
            // Remplie à l'écran mais pas encore au back (il garde ses secondes en reste) :
            // on renvoie le temps à chaque minute jusqu'à ce qu'il la confirme.
            if (progress >= quest.targetValue) {
                finished = true;
                return quest;
            }
            const updated = progress + 1;
            lastProgressRef.current = { ...lastProgressRef.current, [quest.id]: updated };
            fresh[quest.id] = { amount: 1, key: `${quest.id}-${updated}-local`, quiet: true };
            if (updated >= quest.targetValue) finished = true;
            return { ...quest, currentProgress: updated };
        });
        if (Object.keys(fresh).length > 0) {
            questsRef.current = next;
            setQuests(next);
            setGains((current) => ({ ...current, ...fresh }));
        }
        // La quête est remplie à l'écran : on envoie le temps pour qu'elle le soit au back.
        if (finished) flushLearningTime();
    };

    const handleQuestClaim = (questId, data) => {
        // Mettre à jour la quête comme réclamée
        setQuests((prevQuests) =>
            prevQuests.map((quest) =>
                quest.id === questId ? { ...quest, rewarded: true } : quest,
            ),
        );
    };

    if (variant === "pastel") {
        const done = quests.filter((quest) => quest.completed_at || quest.completed).length;
        return (
            <div className={`${PASTEL_SOLID_CARD} p-6`}>
                <div className="mb-5 flex items-center justify-between gap-3">
                    <h2 className="mb-0 flex items-baseline gap-2.5 text-xl font-bold text-slate-900 dark:text-white">
                        {questsTitle}
                        {!loading && quests.length > 0 && (
                            <span className="text-sm font-medium tabular-nums text-slate-400 dark:text-grayDark-500">
                                {done}/{quests.length}
                            </span>
                        )}
                    </h2>
                    <QuestTimer timezone={session?.user?.timezone} variant="pastel" />
                </div>

                <CurrentEventBanner currentEvent={currentEvent} />
            <EventQuestsHeader state={eventState} className="mb-3" />

                <div className="flex flex-col gap-3">
                    {loading ? (
                        <QuestSkeleton />
                    ) : quests.length > 0 ? (
                        quests.map((quest) => (
                            <QuestItem
                                key={quest.id}
                                quest={quest}
                                gain={gains[quest.id]}
                                onComplete={fetchDailyQuests}
                                onClaim={handleQuestClaim}
                            event={liveEvent}
                            pieceLeft={pieceLeft}
                                variant="pastel"
                            />
                        ))
                    ) : (
                        <p className="mb-0 py-4 text-center text-sm text-slate-500 dark:text-grayDark-400">
                            {t("no_quests_available")}
                        </p>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div
            className={cardClass ? cardClass : "border border-gray-200/60 dark:border-borderDark bg-white/80 dark:bg-bgDark-840 backdrop-blur-sm p-4 rounded-2xl shadow-sm"}
            // Pendant un événement, le panneau porte un liseré à sa couleur.
            style={liveEvent?.theme?.color ? { borderColor: `${liveEvent.theme.color}80`, boxShadow: `0 0 0 3px ${liveEvent.theme.color}14` } : undefined}
        >
            <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                    {showIcon && (
                        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-purple-50 dark:bg-purple-500/10">
                            <svg className="w-5 h-5 text-purple-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5}>
                                <path d="M3 8.71c0-1.474 0-2.21.393-2.64a1.5 1.5 0 01.497-.36c.532-.236 1.231-.003 2.629.463c1.067.356 1.6.534 2.14.515a3 3 0 00.588-.078c.525-.125.993-.437 1.929-1.06l1.382-.922c1.2-.8 1.799-1.2 2.487-1.291c.688-.093 1.372.135 2.739.591l1.165.388c.99.33 1.485.495 1.768.888S21 6.12 21 7.162v8.129c0 1.473 0 2.21-.393 2.64a1.5 1.5 0 01-.497.358c-.532.237-1.231.004-2.629-.462c-1.067-.356-1.6-.534-2.14-.515a3 3 0 00-.588.078c-.525.125-.993.437-1.929 1.06l-1.382.922c-1.2.8-1.799 1.2-2.487 1.291c-.688.093-1.372-.135-2.739-.591l-1.165-.388c-.99-.33-1.485-.495-1.768-.888S3 17.88 3 16.838zm6-2.071V20.5M15 3v14" />
                            </svg>
                        </div>
                    )}
                    <div
                        className={`font-bold text-gray-900 dark:text-white ${
                            titleFontSize ? titleFontSize : "text-base"
                        }`}
                    >
                        {questsTitle}
                    </div>
                </div>
                <QuestTimer timezone={session?.user?.timezone} />
            </div>

            <CurrentEventBanner currentEvent={currentEvent} />
            <EventQuestsHeader state={eventState} className="mb-3" />

            <div className="flex flex-col gap-3">
                {loading ? (
                    <QuestSkeleton />
                ) : quests.length > 0 ? (
                    quests.map((quest) => (
                        <QuestItem
                            key={quest.id}
                            quest={quest}
                            gain={gains[quest.id]}
                            onComplete={fetchDailyQuests}
                            onClaim={handleQuestClaim}
                            event={liveEvent}
                            pieceLeft={pieceLeft}
                        />
                    ))
                ) : (
                    <div className="text-center py-4 text-gray-500 dark:text-grayDark-400">
                        {t("no_quests_available")}
                    </div>
                )}
            </div>
        </div>
    );
}
